/**
 * The backsplash and tile bands an elevation can be edited by (call item
 * #28): which of the wall panel's Backsplash-card bands a drawn finish is —
 * a kitchen's counter or range band, a bathroom's shower, vanity or tub band
 * — and which walls one band covers. Pure.
 *
 * A wall's card kind comes from the card's OWN target finder
 * (`backsplashCardTarget`), so the drawing offers exactly the bands the card
 * edits: a hand-drawn kitchen backsplash, a paint or tile section, or a band
 * on a wall with no card stays a plain drawing.
 */
import { isGeneratedKitchenBacksplash } from '@shared-lib/kitchen-assembly/styling/kitchenBacksplash';
import { isOverlayRegion } from '@shared-lib/estimate-pricing/designCenter/surfaceRegions';
import { isWallTileRegion } from '@shared-lib/estimate-pricing/utils/roomTakeOffs';
import { backsplashCardTarget, wallBacksplash } from './backsplashCard';
import {
  bathroomBandOf,
  showerTileWallIds,
  wallBathroomBands,
} from './bathroomBandCard';

/**
 * Which card band `region` is on a wall whose card is `cardKind`
 * ('kitchen' | 'bathroom' | null), or null.
 *
 * @returns {?('counter'|'range'|'shower'|'vanity'|'tub')}
 */
export function cardBandOf(region, cardKind) {
  if (!region) return null;
  if (cardKind === 'kitchen') {
    if (!isGeneratedKitchenBacksplash(region)) return null;
    return region.kind === 'stove' ? 'range' : 'counter';
  }
  if (cardKind === 'bathroom') return bathroomBandOf(region);
  return null;
}

/**
 * The pseudo-band a tile section is drawn as (PR 5): it takes the pointer,
 * the outline and the top-edge grip the card bands do, but opens no card.
 */
export const SECTION_BAND = 'section';

/**
 * Is `region` a TILE SECTION — a wainscot, or tile drawn in Edit wall — whose
 * top edge the drawing drags (PR 5)? A tile overlay (not paint, not the
 * wall's own full-coverage finish, a finish picked, a `regionId`) that no
 * card speaks for:
 * never a shower region (the cloud tiler cuts shower tile, PR 3), a vanity
 * or tub band, or a generated kitchen band — whatever card the wall has, so
 * a host that names no card cannot turn a card's band into a section.
 */
export function isTileSection(region) {
  // without a region id nothing could pick it, drag it or write it back
  if (!region?.regionId) return false;
  if (!isOverlayRegion(region) || !isWallTileRegion(region)) return false;
  return !bathroomBandOf(region) && !isGeneratedKitchenBacksplash(region);
}

/**
 * The tile sections one drag moves (PR 5): the section itself, then every
 * other tile section in the room CREATED with it — the same `groupId`
 * (Yu-Han, 2026-10-22: no height-matching guess). Without a `groupId` it
 * moves alone. Empty when the region is not a tile section.
 *
 * @returns {Array<{wallId: string, regionId: string}>}
 */
export function sectionMembers(room, wallId, regionId) {
  if (!regionId) return [];
  const walls = room?.walls || [];
  const own = walls
    .find((w) => w.id === wallId)
    ?.texture?.regions?.find((r) => r?.regionId === regionId);
  if (!isTileSection(own)) return [];
  const members = [{ wallId, regionId }];
  if (!own.groupId) return members;
  for (const w of walls) {
    for (const r of w?.texture?.regions || []) {
      if (r?.groupId !== own.groupId || !isTileSection(r)) continue;
      if (w.id === wallId && r.regionId === regionId) continue;
      members.push({ wallId: w.id, regionId: r.regionId });
    }
  }
  return members;
}

/**
 * The drawn walls that have a Backsplash card, and which: a Map of the
 * wall's id → `{ roomId, kind }`. A target that resolves to a DIFFERENT
 * wall (a structure-wall id answered by the room wall behind it) is not this
 * drawing's, so it is left out.
 *
 * @param {Array<object>} rooms the live rooms
 * @param {Iterable<string>} wallIds the room walls the drawing shows
 * @param {{windows?: Array, openings?: Array}} [structureOpenings]
 * @returns {Map<string, {roomId: string, kind: 'kitchen'|'bathroom'}>}
 */
export function elevationBandTargets(rooms, wallIds, structureOpenings) {
  const out = new Map();
  for (const wallId of wallIds || []) {
    if (!wallId || out.has(wallId)) continue;
    const target = backsplashCardTarget(rooms, wallId, null, structureOpenings);
    if (target && target.wallId === wallId) {
      out.set(wallId, { roomId: target.room.id, kind: target.kind });
    }
  }
  return out;
}

/**
 * The bands a wall's card has, in the order the card draws them — the bands
 * a click on the drawing can pick there. A kitchen wall: over the counter,
 * and behind the range when it has one; a bathroom wall: whichever of its
 * shower, vanity and tub sections the card draws.
 */
export function cardBandsOn(room, kind, wallId, structureOpenings) {
  if (kind === 'kitchen') {
    const facts = wallBacksplash(room, wallId, structureOpenings);
    return facts ? ['counter', ...(facts.hasRange ? ['range'] : [])] : [];
  }
  const facts = wallBathroomBands(room, wallId);
  return ['shower', 'vanity', 'tub'].filter((band) => facts?.[band]);
}

/**
 * The walls one band covers, this wall first: a kitchen band is its own
 * wall's; a vanity or tub band runs on to every wall its fixture's band
 * spans (the card's "Also changes"); the shower's tile height is the room's,
 * so it covers every wall carrying shower tile.
 */
export function bandWallIds(room, wallId, band) {
  if (band === 'vanity' || band === 'tub') {
    const facts = wallBathroomBands(room, wallId)?.[band];
    return [wallId, ...(facts?.alsoWalls || []).map((w) => w.id)];
  }
  if (band === 'shower') {
    return [wallId, ...showerTileWallIds(room).filter((id) => id !== wallId)];
  }
  return [wallId];
}

/**
 * Which of the viewer's elevations draws `wallId` — its own, or a grouped
 * one it is a member of. -1 when none does.
 */
export function elevationIndexOfWall(elevations, wallId) {
  if (!Array.isArray(elevations) || !wallId) return -1;
  return elevations.findIndex(
    (e) =>
      e?.id === wallId ||
      (Array.isArray(e?.groupedWalls) &&
        e.groupedWalls.some((g) => g?.id === wallId))
  );
}

/**
 * The room walls one elevation draws: its own wall, or each member of a
 * grouped one. A free-standing leg's face names no room wall, so it carries
 * no band.
 */
export function elevationWallIds(elevation) {
  if (!elevation) return [];
  if (elevation.isGrouped && Array.isArray(elevation.groupedWalls)) {
    return elevation.groupedWalls.map((w) => w?.id).filter(Boolean);
  }
  return elevation.id ? [elevation.id] : [];
}

// ---- the drawn pieces of a band, in px (top-left origin)

/** Two px values this close are one edge. */
export const EDGE_PX = 1.5;
const overlaps = (a0, a1, b0, b1) =>
  Math.min(a1, b1) - Math.max(a0, b0) > EDGE_PX;

/**
 * Is this drawn piece's top a window's sill — a piece cut under the glass,
 * whose top no setting moves? Its top edge is then not something to drag.
 *
 * @param {{left, top, width, height}} piece px box
 * @param {Array<{left, top, width, height}>} openings the windows' px boxes
 * @param {number} [tolPx] how far apart still counts as meeting — the band
 *   builder stops a piece 1 cm under the sill (`WINDOW_BAND_Y_BUFFER_M`), so
 *   a host passes that at its drawing scale
 */
export function topMeetsOpening(piece, openings, tolPx = EDGE_PX) {
  return (openings || []).some(
    (o) =>
      Math.abs(o.top + o.height - piece.top) <= tolPx &&
      overlaps(piece.left, piece.left + piece.width, o.left, o.left + o.width)
  );
}

/**
 * Is this side of a drawn piece cut at a window's glass?
 *
 * @param {{left, top, width, height}} piece px box
 * @param {'left'|'right'} side
 * @param {Array<{left, top, width, height}>} openings
 * @param {number} [tolPx] the gap under the glass that still counts as
 *   touching it (see `topMeetsOpening`)
 */
export function sideMeetsOpening(piece, side, openings, tolPx = EDGE_PX) {
  const x = side === 'left' ? piece.left : piece.left + piece.width;
  return (openings || []).some(
    (o) =>
      // on either edge of the glass: a piece beside it, or one cut below it
      (Math.abs(o.left - x) <= EDGE_PX ||
        Math.abs(o.left + o.width - x) <= EDGE_PX) &&
      // level with the glass, or touching it from below
      Math.min(piece.top + piece.height, o.top + o.height) -
        Math.max(piece.top, o.top) >=
        -tolPx
  );
}

/** Why a band's sides do not drag (v1), by band. */
export const BAND_SIDE_HINTS = Object.freeze({
  counter: 'Sides follow the cabinets. Not draggable.',
  range: 'Sides follow the range. Not draggable.',
  shower: 'Shower tile runs 2″ past the shower. Not draggable.',
  vanity: 'Sides follow the vanity. Not draggable.',
  tub: 'Sides follow the tub. Not draggable.',
  section: 'Sides are set in Edit wall. Not draggable here.',
});
/** A side cut round the window glass. */
export const GLASS_SIDE_HINT = 'Cut round the window glass.';
